import { Box, Card, CardContent, LinearProgress, Typography } from "@mui/material";
import type { JSX } from "react";

import type { UsageSummary } from "../types.js";
import { formatUsd, providerLabel } from "./format.js";

function SpendHeading({ month }: { month: string | null }): JSX.Element {
  return (
    <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
      Spend{month ? ` · ${month}` : ""}
    </Typography>
  );
}

/** Month-to-date LLM spend, metered from our own gateway calls. Extracted from
 * `ProvidersPanel.tsx`. */
export function SpendSection({
  usage,
  error,
}: {
  usage: UsageSummary | null;
  error: string | null;
}): JSX.Element {
  if (error) {
    return (
      <Card>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <SpendHeading month={null} />
          <Typography sx={{ mt: 0.5, fontSize: 14, color: "text.disabled" }}>
            Couldn’t load spend: {error}
          </Typography>
        </CardContent>
      </Card>
    );
  }
  if (!usage) {
    return (
      <Card>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <SpendHeading month={null} />
          <Typography sx={{ mt: 0.5, fontSize: 14, color: "text.disabled" }}>Loading…</Typography>
        </CardContent>
      </Card>
    );
  }

  const hasBudget = usage.budget_usd > 0;
  const pct = usage.pct_used ?? 0;
  const remaining = Math.max(0, usage.budget_usd - usage.total_usd);

  const groups: { title: string; rows: BreakdownRow[] }[] = [
    { title: "By agent", rows: usage.by_agent.map((a) => ({ key: a.caller_app, label: a.caller_app, cost: a.cost_usd })) },
    { title: "By provider", rows: usage.by_provider.map((p) => ({ key: p.provider, label: providerLabel(p.provider), cost: p.cost_usd })) },
    { title: "By model", rows: usage.by_model.map((m) => ({ key: `${m.provider}/${m.model}/${m.kind}`, label: `${m.model} · ${m.kind}`, cost: m.cost_usd })) },
  ].filter((g) => g.rows.length > 0);

  return (
    <Card>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        {/* Total + (optional) budget, side by side. */}
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", columnGap: 3, rowGap: 1.5 }}>
          <Box>
            <SpendHeading month={usage.month} />
            <Typography sx={{ mt: 0.5, fontSize: "1.875rem", fontWeight: 600, fontVariantNumeric: "tabular-nums", color: "text.primary" }}>
              {formatUsd(usage.total_usd)}
            </Typography>
          </Box>
          {hasBudget && (
            <Box sx={{ minWidth: "14rem", flex: 1, maxWidth: { sm: "20rem" } }}>
              <Box sx={{ mb: 0.5, display: "flex", alignItems: "baseline", justifyContent: "space-between", fontSize: 12 }}>
                <Box component="span" sx={{ color: "text.disabled" }}>
                  {pct.toFixed(1)}% of {formatUsd(usage.budget_usd)}
                </Box>
                <Box component="span" sx={{ fontVariantNumeric: "tabular-nums", color: "text.primary" }}>
                  {formatUsd(remaining)} left
                </Box>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, Math.max(0, pct))}
                sx={{
                  height: 8,
                  borderRadius: 999,
                  bgcolor: "surfaceSoft",
                  "& .MuiLinearProgress-bar": { borderRadius: 999, bgcolor: "primary.main" },
                }}
              />
            </Box>
          )}
        </Box>

        {usage.total_usd === 0 ? (
          <Typography sx={{ mt: 2, fontSize: 14, color: "text.disabled" }}>
            No spend recorded yet this month. Metering is forward-only — figures
            fill in as the agents make calls.
          </Typography>
        ) : (
          <Box
            sx={{
              mt: 2.5,
              display: "grid",
              columnGap: 5,
              rowGap: 3,
              borderTop: 1,
              borderColor: "divider",
              pt: 2.5,
              gridTemplateColumns: { sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
            }}
          >
            {groups.map((g) => (
              <Breakdown key={g.title} title={g.title} rows={g.rows} total={usage.total_usd} />
            ))}
          </Box>
        )}

        <Typography sx={{ mt: 2.5, fontSize: 11, color: "text.disabled" }}>
          Prices as of {usage.prices_as_of}. No live account-balance API —
          metered from our own gateway calls.
          {usage.unpriced_models.length > 0 && (
            <Box component="span" sx={{ color: "warning.main" }}>
              {" "}· Unpriced: {usage.unpriced_models.join(", ")}
            </Box>
          )}
        </Typography>
      </CardContent>
    </Card>
  );
}

interface BreakdownRow {
  key: string;
  label: string;
  cost: number;
}

/**
 * A spend breakdown as a compact list with proportion bars — each bar is the
 * row's share of the month's total, so the biggest line items read at a glance
 * instead of scanning a wide table. Rows arrive pre-sorted by cost (the gateway
 * sorts them).
 */
function Breakdown({
  title,
  rows,
  total,
}: {
  title: string;
  rows: BreakdownRow[];
  total: number;
}): JSX.Element {
  return (
    <Box sx={{ display: "grid", gap: 1.25 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
        {title}
      </Typography>
      <Box sx={{ display: "grid", gap: 1 }}>
        {rows.map((r) => {
          const share = total > 0 ? (r.cost / total) * 100 : 0;
          return (
            <Box key={r.key} sx={{ display: "grid", gap: 0.5 }}>
              <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
                <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "ui-monospace, monospace", fontSize: 12, color: "text.disabled" }}>
                  {r.label}
                </Box>
                <Box component="span" sx={{ flexShrink: 0, fontSize: 12, fontVariantNumeric: "tabular-nums", color: "text.primary" }}>
                  {formatUsd(r.cost)}
                </Box>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.max(2, Math.min(100, share))}
                sx={{
                  height: 6,
                  borderRadius: 999,
                  bgcolor: "surfaceSoft",
                  "& .MuiLinearProgress-bar": { borderRadius: 999, bgcolor: "primary.main" },
                }}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
